import React, { useEffect, useState } from 'react';
import { DetailItem } from 'app/shared/info-section/item';
import { toast } from 'react-toastify';
import { CodeWrapper } from 'app/shared/wrappers/code';
import { Spacer } from 'app/layouts/generic';
import GmModal from 'app/shared/modal/modal';
import styles from './dkim.module.css';
import { InfoBanner } from 'app/shared/info-banner';
import { GridColumn, GridRow } from 'app/layouts/grid';
import { Button } from 'app/shared/button';
import { useMailingConfigService } from 'hooks/mailing/configuration';

const DNSRecordDisplay = ({ email_config }) => {
  const [dkim, setDKIM] = useState({});
  const [spf, setSPF] = useState({});

  useEffect(() => {
    if (email_config?.dkim) setDKIM(email_config.dkim);
    if (email_config?.spf) setSPF(email_config.spf);
  }, [email_config]);

  const handleCopy = (snippet) => {
    navigator.clipboard.writeText(snippet).then(() => toast.info('Copied.'));
  };

  const handleCopyAllValues = () => {
    const spfHost = email_config?.domain;
    const spfValue = spf?.value;
    const dkimSelector = `${dkim?.selector}._domainkey`;
    const dkimValue = dkim?.value;

    const formattedValues = `
      [SPF RECORD]
      Type: TXT
      Host: ${spfHost}
      Value: ${spfValue}

      !IMPORTANT Please note that you cannot have multiple SPF records for the Same host value. If you have an existing record for ${spfHost}, simply replace the existing value with this one.
    
      [DKIM RECORDS]
      Type: TXT
      Host: ${dkimSelector}
      Value: ${dkimValue}
      `.trim();

    navigator.clipboard.writeText(formattedValues).then(() => toast.info('Copied.'));
  };

  return (
    <>
      <InfoBanner message="To improve your email deliverability, you need to configure these DNS records on your Domain's cpanel." />
      <Spacer multiple={4} />
      <GridRow num_of_columns={2}>
        <GridColumn>
          <DetailItem title="SPF Record Type">
            <CodeWrapper text="TXT" onClick={handleCopy} info_text="click on record to copy" />
          </DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="SPF Record Host">
            <CodeWrapper
              text={email_config?.domain}
              onClick={handleCopy}
              info_text="click on record to copy"
            />
          </DetailItem>
        </GridColumn>
      </GridRow>
      <GridRow num_of_columns={1}>
        <GridColumn>
          <DetailItem title="SPF Record Value">
            <CodeWrapper
              text={spf?.value}
              onClick={handleCopy}
              info_text="click on record to copy"
            />
          </DetailItem>
        </GridColumn>
      </GridRow>
      <Spacer multiple={2} />
      <hr />
      <Spacer multiple={4} />
      <GridRow num_of_columns={2}>
        <GridColumn>
          <DetailItem title="DKIM Record Type">
            <CodeWrapper text="TXT" />
          </DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="DKIM Record Selector/Name">
            <CodeWrapper text={`${dkim?.selector}._domainkey`} />
          </DetailItem>
        </GridColumn>
      </GridRow>
      <GridRow num_of_columns={1}>
        <GridColumn>
          <DetailItem title="DKIM Record">
            <CodeWrapper
              text={dkim?.value}
              onClick={handleCopy}
              info_text="click on record to copy"
            />
          </DetailItem>
        </GridColumn>
      </GridRow>
      <Spacer multiple={2} />
      <GridRow num_of_columns={1}>
        <GridColumn>
          <Button
            text="Copy all values"
            onClick={handleCopyAllValues}
            className="float-right w-25"
          />
        </GridColumn>
      </GridRow>
    </>
  );
};

export const DKIMConfigurationDetails = ({ email_config, onDelete = () => {} }) => {
  const { validateRecords } = useMailingConfigService();

  const [is_verifying, setIsVerifying] = useState(false);
  const [show_details, setShowDetails] = useState(false);

  const handleRecordValidation = async () => {
    setIsVerifying(true);
    const result = await validateRecords({ data: { domain: email_config.domain } });
    if (result.error) {
      toast.error(result.error);
    } else {
      toast.success('DNS records validated successfully.');
    }
    setIsVerifying(false);
  };

  return (
    <>
      <div className={styles.wrapper}>
        <span className={styles.title}>{email_config.domain || 'N/A'}</span>
        <span className={styles.actions}>
          <Button
            text="verify"
            onClick={handleRecordValidation}
            className={styles.verifyBtn}
            disabled={is_verifying}
          />
          <Button text="view" onClick={() => setShowDetails(true)} className={styles.viewBtn} />
          <Button
            text="Delete"
            onClick={() => onDelete(email_config)}
            className={styles.deleteBtn}
          />
        </span>
      </div>
      <GmModal
        show_modal={show_details}
        show_title
        title={`DNS records for ${email_config.domain}`}
        onClose={() => setShowDetails(false)}
      >
        <DNSRecordDisplay email_config={email_config} />
      </GmModal>
    </>
  );
};
